import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import {
    Admin,
    PublishingAuthor,
} from '@deltek/specification-client/core/Configurations/Authorization'
import {
    GetDesignatedAdminRoutingComponentProps,
    getDesignatedAdminRoutingComponent,
} from '@deltek/specification-client/core/Configurations/DesignatedAdminRouting'
import {
    GlobalSnackbar,
    GlobalSnackbarPropsQuery,
    GlobalSnackbarPropsQueryResponse,
    HideGlobalSnackbarMutation,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import ContentContainer from '@deltek/specification-client/core/Containers/ContentContainer'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import {
    ILoadingPageContext,
    LoadingPageContext,
} from '@deltek/specification-client/core/Contexts/LoadingPageContext'
import {
    CurrentUserActiveFirmQuery,
    GetReleaseNoteUriQuery,
} from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import LowerToolbar from '@deltek/specification-client/core/Headers/LowerToolbar'
import TopBarTools from '@deltek/specification-client/core/Headers/TopBarTools'
import ResponsiveMenus from '@deltek/specification-client/core/Menus/ResponsiveMenus'
import Sidebar from '@deltek/specification-client/core/Menus/Sidebar'
import {
    CurrentUserActiveFirmResponse,
    CurrentUserFirmsProps,
    MasterSpecNoteUriResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { FirmId } from '@deltek/specification-client/core/Models/firm'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { FirmUserResponse } from '@deltek/specification-client/core/Models/user'
import TermsAndConditions from '@deltek/specification-client/core/Pages/TermsAndConditions'
import DoubleAppToolbar from '@deltek/specification-client/core/Surfaces/DoubleAppToolbar'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { isDeltekUserorAdmin } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { AcceptTermsAndConditionsMutation } from 'api/graphQL/mutations/user.mutations'
import {
    FirmDesignatedAccountAdminQuery,
    FirmSupportContactUserQuery,
} from 'api/graphQL/queries/firm.queries'
import { UserTAndCQuery } from 'api/graphQL/queries/user.queries'
import { UserProfileQuery } from 'api/graphQL/queries/userProfile.queries'
import { FirmResponse } from 'api/graphQL/types/firm.types'
import UserProfileUpdate from 'components/UserProfileUpdate'
import {
    isProduction,
    processEnv,
} from 'components/configuration/environmentVariables'
import MainPageRouting from 'pages/MainPage/MainPageRouting'
import { isUserBPMAdmin } from 'pages/ProductsPage/productsUtils'
import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { Routes } from 'routes'
import {
    generateOnlineHelpUrl,
    getHelpDeskEmailAddress,
    isUserDesignatedAccountAdmin,
    useSidebarItems,
} from './mainPageUtils'

const bpmReleaseNotesUrl =
    'https://dsm.deltek.com/DeltekSoftwareManagerWebServices/downloadFile.ashx?documentid=005B92D0-4360-42CD-A97B-1E7F3CA008F1'

const MainPage: React.FC = () => {
    const client = useApolloClient()
    const location = useLocation()
    const history = useHistory()
    const theme = useTheme()

    const currentUserInfoResponse = useContext(AuthContext)
    const { logOut, changePassword, currentUserInfo } = currentUserInfoResponse

    const { data: currentUserData } = useQuery<FirmUserResponse>(
        UserProfileQuery,
        {
            variables: {
                firmId: currentUserInfo?.account?.accountId ?? '',
                userId: currentUserInfo?.kpUserId ?? '',
            },
            skip: !currentUserInfo,
        }
    )

    const { data: masterSpecReleaseNoteUri } = useQuery<
        MasterSpecNoteUriResponse
    >(GetReleaseNoteUriQuery, {
        variables: {
            input: {
                releaseNoteName: 'MASTERSPEC',
            },
        },
    })

    const userRole = React.useMemo(() => currentUserInfo?.roles[0] ?? '', [
        currentUserInfo,
    ])

    const {
        data: firmSupportContactData,
        loading: firmSupportContactLoading,
    } = useQuery<FirmResponse, FirmId>(FirmSupportContactUserQuery, {
        variables: {
            firmId: currentUserInfo?.account?.accountId ?? '',
        },
        skip: !currentUserInfo,
    })

    useEffect(() => {
        // @ts-ignore
        window.aptrinsic(
            'identify',
            {
                //User Fields
                id: currentUserInfo?.kpUserId ?? '',
                email: currentUserInfo?.email ?? '',
                firstName: currentUserInfo?.firstName ?? '',
                lastName: currentUserInfo?.lastName ?? '',
                role: currentUserInfo?.roles[0] ?? '',
                accountId: currentUserInfo?.account?.accountId ?? '',
                sppt_app: 'BPM',
                sppt_firmName: firmSupportContactData?.firm?.name,
                spptUserManagement: `${currentUserInfo?.roles} ${currentUserData?.user?.profileInfo?.professionalDiscipline} BPG:${currentUserData?.user?.isBestPracticeGroupEnabled}`,
            },
            {
                sfdcId:
                    firmSupportContactData?.firm?.subscriptionInfo
                        ?.salesforceId ?? '',
            }
        )
    }, [currentUserInfo, firmSupportContactData])

    const { data: globalSnackbarPropsData } = useQuery<
        GlobalSnackbarPropsQueryResponse
    >(GlobalSnackbarPropsQuery)
    const [
        getFirmDesignatedAdminUserId,
        {
            called: firmDesignatedAdminCalled,
            loading: firmDesignatedAdminLoading,
            data: firmDesignatedAdminResponse,
        },
    ] = useLazyQuery<FirmResponse>(FirmDesignatedAccountAdminQuery, {
        fetchPolicy: 'no-cache',
    })

    const contentContainerRef = useRef<HTMLDivElement>(null)
    const [hideGlobalSnackbar] = useMutation(HideGlobalSnackbarMutation)
    let smScreen = useMediaQuery(theme.breakpoints.up('sm'))
    const [sidebarOpen, setSidebarOpen] = React.useState(!smScreen)
    const [userProfileClicked, setUserProfileClicked] = React.useState<boolean>(
        false
    )

    const [userFirmDetail, setUserFirmDetail] = React.useState<
        CurrentUserFirmsProps
    >()

    const {
        customComponent,
        pageTitle,
        customTextColor,
        barColor,
        hasLowerBottomToolbar,
        setHasLowerBottomToolbar,
        fullHeight,
        isCustomToolbar,
    } = React.useContext(AppHeaderContext)

    const toggleSidebarOpen = React.useCallback(
        (newValue: boolean | 'toggle') => {
            if (newValue !== 'toggle') setSidebarOpen(newValue)
            else setSidebarOpen(!sidebarOpen)
        },
        [sidebarOpen]
    )
    const onUserProfileClick = useCallback(() => {
        setUserProfileClicked(true)
    }, [])

    const onFirmSelect = useCallback(() => {
        window.localStorage.clear()
        window.open(userFirmDetail?.firmAccountTypeApplicationURL, '_self')
    }, [userFirmDetail])

    const getUserFirm = useCallback((data: CurrentUserFirmsProps) => {
        setUserFirmDetail(data)
    }, [])

    const helpDeskEmailAddress: string = React.useMemo(() => {
        if (!firmSupportContactLoading && firmSupportContactData) {
            return getHelpDeskEmailAddress(
                currentUserInfo!,
                firmSupportContactData?.firm
            )
        }
        return ''
    }, [firmSupportContactLoading, firmSupportContactData, currentUserInfo])

    const userIsDesignatedSupportPerson: boolean = React.useMemo(() => {
        return isUserDesignatedAccountAdmin(
            currentUserInfo!,
            firmSupportContactData?.firm!
        )
    }, [currentUserInfo, firmSupportContactData])

    const hideIndustryAnalytics: boolean = React.useMemo(() => {
        return !firmSupportContactLoading && firmSupportContactData
            ? firmSupportContactData?.firm?.hideIndustryAnalytics ?? false
            : false
    }, [firmSupportContactLoading, firmSupportContactData])

    const { data: currentUserActiveFirmResponse } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery)

    const onlineHelpUrl: string = React.useMemo(() => {
        let userAccountType = currentUserInfo?.account.accountType ?? ''
        if (
            userAccountType === 'System' &&
            currentUserActiveFirmResponse?.myActiveFirm.firmAccountType ===
                'AEFirm'
        ) {
            userAccountType = 'AEFirm'
        }
        return generateOnlineHelpUrl(userAccountType)
    }, [currentUserActiveFirmResponse, currentUserInfo, generateOnlineHelpUrl])

    const {
        isDeltekUser,
        isDeltekAdmin,
        isDeltekAuthor,
        isDeltekReviewer,
    } = isDeltekUserorAdmin(currentUserInfoResponse)

    const isPublishingAuthor = React.useMemo(
        () => currentUserInfo?.roles.includes(PublishingAuthor) ?? false,
        [currentUserInfoResponse]
    )

    const isBPM = React.useMemo(
        () => !!isUserBPMAdmin(currentUserInfoResponse).isBPM,
        [currentUserInfoResponse]
    )

    const sidebarItems = useSidebarItems({
        userRole,
        isBPM,
        isDeltekUser,
        isDeltekAdmin,
        isDeltekAuthor,
        isPublishingAuthor,
        isDeltekReviewer,
        hideIndustryAnalytics,
        hasCompanyAccountAccess: !!currentUserData?.user?.isOnboarder,
    })

    const helpMenuItems: MenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'globalHelpMenuItemOnlineHelp',
                label: 'Online Help',
                onClick: () => navigateToExternalLink?.(onlineHelpUrl),
            },
            {
                dataTestId: 'globalHelpMenuItemReleaseNotes',
                label: 'Release Notes',
                onClick: () => navigateToExternalLink?.(bpmReleaseNotesUrl),
            },
            {
                dataTestId: 'globalMasterSpecReleaseNotes',
                label: 'MasterSpec Updates',
                onClick: () =>
                    navigateToExternalLink?.(
                        masterSpecReleaseNoteUri?.getReleaseNoteUri ?? '',
                        true
                    ),
            },
            {
                dataTestId: 'globalHelpMenuItemEmailHelpDesk',
                label: 'Email Help Desk',
                onClick: () =>
                    navigateToExternalLink?.(
                        `mailto:${helpDeskEmailAddress}`,
                        true
                    ),
            },
            ...(userIsDesignatedSupportPerson
                ? [
                      {
                          dataTestId: 'globalHelpMenuItemDesktopSupportCenter',
                          label: 'Deltek Support Center',
                          onClick: () =>
                              navigateToExternalLink?.(
                                  'https://deltek.custhelp.com'
                              ),
                      },
                  ]
                : []),
            {
                label: 'Privacy Policy',
                dataTestId: 'globalHelpMenuItemPrivacyPolicy',
                onClick: () =>
                    navigateToExternalLink?.('https://www.deltek.com/en/legal'),
            },
            {
                label: 'Terms of Use',
                dataTestId: 'globalHelpMenuItemTermsOfUse',
                onClick: () =>
                    navigateToExternalLink?.(
                        'https://www.deltek.com/en/deltek-contracts/masterspec'
                    ),
            },
        ],
        [
            helpDeskEmailAddress,
            userIsDesignatedSupportPerson,
            onlineHelpUrl,
            masterSpecReleaseNoteUri,
        ]
    )

    const responsiveMenus = React.useMemo(() => {
        return (
            <ResponsiveMenus
                accountType={currentUserInfo?.account.accountType}
                showProductionOnlineHelp={isProduction}
                onUserProfileClick={onUserProfileClick}
                userProfileImage={currentUserData?.user?.profileInfo?.imageUrl}
                changePassword={changePassword}
                logout={logOut}
                userName={{
                    first: currentUserInfo?.firstName,
                    last: currentUserInfo?.lastName,
                    userId: currentUserInfo?.kpUserId,
                }}
                onFirmSelect={onFirmSelect}
                showMultiFirm={true}
                buildNumber={processEnv.REACT_APP_BuildNumber}
                helpMenuItems={helpMenuItems}
                releaseNoteUrl={masterSpecReleaseNoteUri?.getReleaseNoteUri}
                getUserFirms={getUserFirm}
                pollInterval={parseInt(processEnv.REACT_APP_NotifInterval)}
            />
        )
    }, [
        helpMenuItems,
        logOut,
        currentUserInfo,
        changePassword,
        firmSupportContactData,
        currentUserData,
        onUserProfileClick,
        onFirmSelect,
    ])

    const { handleLoadingPageChange, showLoadingScreenPage } = useContext<
        ILoadingPageContext
    >(LoadingPageContext)
    const [isTermsAccepted, setIsTermsAccepted] = React.useState<
        boolean | undefined
    >()

    const { data: userTandCDate, loading: userTandCDateLoading } = useQuery(
        UserTAndCQuery,
        {
            onCompleted: () => {
                if (
                    userTandCDate &&
                    userTandCDate.myTermsAcceptanceDate !== ''
                ) {
                    setIsTermsAccepted(true)
                }
            },
        }
    )

    const [acceptTermsAndConditions] = useMutation(
        AcceptTermsAndConditionsMutation
    )

    const handleDialogClose = () => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.has('newuser')) {
            searchParams.delete('newuser')
            history.replace({
                ...location,
                search: `?${searchParams.toString()}`,
            })
        }
        setUserProfileClicked(false)
    }

    const handleTermsDialogDisplay = React.useCallback(() => {
        setIsTermsAccepted(!isTermsAccepted)
        acceptTermsAndConditions()

        onUserProfileClick()
    }, [
        isTermsAccepted,
        setIsTermsAccepted,
        acceptTermsAndConditions,
        onUserProfileClick,
    ])

    const logoOnly = React.useMemo(
        () => (location.pathname.includes('/contacts/details/') ? true : false),
        [location]
    )

    const upperChildren = React.useMemo(
        () => (
            <TopBarTools
                toggleSidebarOpen={toggleSidebarOpen}
                logoOnly={logoOnly}
            >
                {smScreen && responsiveMenus}
            </TopBarTools>
        ),
        [toggleSidebarOpen, smScreen, responsiveMenus, logoOnly]
    )

    const lowerChildren = React.useMemo(
        () => (
            <LowerToolbar
                customText={pageTitle}
                customTextColor={customTextColor}
                lowerBarBackgroundColor={barColor}
                customComponent={customComponent}
                routes={Routes}
                setHasLowerBottomToolbar={setHasLowerBottomToolbar}
                hasLowerBottomToolbar={hasLowerBottomToolbar}
                fullHeight={fullHeight}
                isCustomToolbar={isCustomToolbar}
            />
        ),
        [
            pageTitle,
            customTextColor,
            barColor,
            customComponent,
            setHasLowerBottomToolbar,
            hasLowerBottomToolbar,
            fullHeight,
            isCustomToolbar,
        ]
    )

    const designatedAdminRoutingProps: GetDesignatedAdminRoutingComponentProps | null = React.useMemo(() => {
        if (currentUserInfoResponse && !firmDesignatedAdminLoading) {
            return {
                location: {
                    ...location,
                },
                firmId:
                    currentUserInfoResponse.currentUserInfo?.account.accountId,
                kpUserId: currentUserInfoResponse.currentUserInfo?.kpUserId,
                getFirmDesignatedAdminUserId,
                firmDesignatedAdminCalled,
                firm: firmDesignatedAdminResponse?.firm,
            }
        }
        return null
    }, [
        firmDesignatedAdminResponse,
        currentUserInfo,
        firmDesignatedAdminCalled,
        getFirmDesignatedAdminUserId,
        location,
        firmDesignatedAdminLoading,
    ])

    const designatedAdminRoutingComponent = React.useMemo(() => {
        if (!designatedAdminRoutingProps) return null

        return getDesignatedAdminRoutingComponent(designatedAdminRoutingProps)
    }, [designatedAdminRoutingProps])

    useEffect(() => {
        if (
            location.pathname === '/account' &&
            !!currentUserInfo &&
            !firmDesignatedAdminCalled
        ) {
            getFirmDesignatedAdminUserId({
                variables: {
                    id: currentUserInfo?.account.accountId,
                },
            })
        }
    }, [
        location,
        currentUserInfo,
        getFirmDesignatedAdminUserId,
        firmDesignatedAdminCalled,
    ])

    useEffect(() => {
        if (!handleLoadingPageChange) return

        if (!client || !currentUserInfo) {
            handleLoadingPageChange(true)
            return
        }

        // hide any open snackbar on page refresh
        if (history.action === 'POP' && !!hideGlobalSnackbar)
            hideGlobalSnackbar()

        if (!location.search.includes('newuser=1')) {
            handleLoadingPageChange(false)
            return
        }

        // if it includes admin as a role, redirect to /account and open the dialog
        if (currentUserInfo?.roles.includes(Admin) ?? false) {
            handleLoadingPageChange(!firmDesignatedAdminResponse)
            return
        }

        // if the user is not an admin, open the dialog where they get redirected
        handleLoadingPageChange(false)
    }, [
        client,
        currentUserInfo,
        showLoadingScreenPage,
        firmDesignatedAdminResponse,
        onUserProfileClick,
        handleLoadingPageChange,
        hideGlobalSnackbar,
        location,
        history.action,
    ])

    useLayoutEffect(() => {
        contentContainerRef.current?.scrollTo(0, 0)
    }, [location.pathname, contentContainerRef])

    return showLoadingScreenPage ||
        (!firmSupportContactLoading &&
            location.search.includes('newuser=1') &&
            currentUserInfo?.roles.includes(Admin) &&
            !!designatedAdminRoutingComponent) ? (
        designatedAdminRoutingComponent
    ) : !!hideIndustryAnalytics && location.pathname === '/analytics/legacy' ? (
        <Redirect
            to={{
                pathname: location.pathname,
                state: { forbidden: true },
            }}
        />
    ) : (
        <div>
            <DoubleAppToolbar
                UpperChildren={upperChildren}
                LowerChildren={lowerChildren}
            ></DoubleAppToolbar>
            <Sidebar
                open={sidebarOpen}
                updateSidebarVisibility={toggleSidebarOpen}
                bottomContent={!smScreen && responsiveMenus}
                items={sidebarItems}
            />
            <ContentContainer
                top={hasLowerBottomToolbar ? 150 : 100}
                ref={contentContainerRef}
            >
                <MainPageRouting
                    routes={Routes}
                    userRoles={currentUserInfo?.roles ?? []}
                />
                {!userTandCDateLoading && !isTermsAccepted && (
                    <TermsAndConditions
                        isOpen={!isTermsAccepted}
                        handleDialogClose={handleTermsDialogDisplay}
                    ></TermsAndConditions>
                )}
                {!userTandCDateLoading && isTermsAccepted && (
                    <UserProfileUpdate
                        userProfileClicked={userProfileClicked}
                        handleDialogClose={handleDialogClose}
                    />
                )}
            </ContentContainer>
            {globalSnackbarPropsData && (
                <GlobalSnackbar
                    hideGlobalSnackbar={hideGlobalSnackbar}
                    {...globalSnackbarPropsData.globalSnackbarProps}
                />
            )}
        </div>
    )
}
export default MainPage
